import Skeleton from '@components/v2/ui/Skeleton/Skeleton';
import { Stack, useTheme } from '@mui/material';
import useGeneralSettingsStore from '../../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import { MediaFormatEnum } from '../../../../../general-settings/enums/mediaFormat.enum';
import { useOrderProductsLayoutContext } from '../../../../contexts/OrderProductsLayout.context';
import { useOrderProductItemStyles } from '../../../../styles/useOrderProductItemStyles';
import { OrderProductSelectionEdge } from '../../../orderProducts.types';
import { ORDER_PRODUCT_SELECT_SIZE_BY_LAYOUT } from '../../../orderProductsLayout.constants';
import { OrderProductRowLayout } from '../../../orderProductsLayout.types';
import OrderProductGridSkeleton from './OrderProductGridSkeleton';
import OrderProductTotalsSkeleton from './OrderProductTotalsSkeleton';
import {
  SKELETON_ACTIONS_SIZE,
  SKELETON_CHECKBOX_RADIUS,
  SKELETON_CHECKBOX_SIZE,
  SKELETON_CHECKBOX_SLOT_SIZE,
  SKELETON_IMAGE_GAP,
  SKELETON_IMAGE_HEIGHT,
  SKELETON_IMAGE_SQUARE_WIDTH,
  SKELETON_IMAGE_WIDTH,
  SKELETON_INFO_ROW_GAP,
  SKELETON_LABEL_HEIGHT,
  SKELETON_SURFACE_SX,
  SKELETON_TAG_HEIGHT,
  SKELETON_TAG_WIDTHS,
} from './orderProductItemSkeleton.constants';

interface OrderProductItemSkeletonProps {
  canEditOrder?: boolean;
  selectionEdge?: OrderProductSelectionEdge;
  isLastOrderProduct?: boolean;
}

const OrderProductItemSkeleton = ({
  canEditOrder = true,
  selectionEdge,
  isLastOrderProduct,
}: OrderProductItemSkeletonProps) => {
  const theme = useTheme();
  const { layout, hasTouchGrid } = useOrderProductsLayoutContext();
  const { classes, cx } = useOrderProductItemStyles({
    layout,
    hasTouchGrid,
    selectionEdge,
    isLastOrderProduct,
  });
  const {
    generalSettings: { mediaFormat },
  } = useGeneralSettingsStore();

  const imageWidth =
    mediaFormat === MediaFormatEnum.SQUARE ? SKELETON_IMAGE_SQUARE_WIDTH : SKELETON_IMAGE_WIDTH;
  const selectHeight = ORDER_PRODUCT_SELECT_SIZE_BY_LAYOUT[layout].height;
  const infoRadiusSx = {
    ...SKELETON_SURFACE_SX,
    borderRadius: `${theme.shape.borderRadiusMd}px`,
  };

  const isMobileLayout = layout === OrderProductRowLayout.MOBILE;
  const isInlineLayout = layout === OrderProductRowLayout.WIDE;

  return (
    <Stack
      aria-hidden
      className={cx(classes.root, classes.fixedRowHeight)}
      direction="row"
      sx={{
        alignItems: isMobileLayout ? 'flex-start' : 'center',
        alignContent: 'flex-start',
        flexWrap: isInlineLayout ? 'nowrap' : 'wrap',
      }}
    >
      <div className={classes.orderProductInfoContainer}>
        <Stack
          className={classes.orderProductInfo}
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: isMobileLayout ? 'wrap' : 'nowrap',
          }}
        >
          <div className={classes.orderProductImageAndLabel}>
            {canEditOrder && (
              <Stack
                sx={{
                  flexShrink: 0,
                  width: SKELETON_CHECKBOX_SLOT_SIZE,
                  height: SKELETON_CHECKBOX_SLOT_SIZE,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width={SKELETON_CHECKBOX_SIZE}
                  height={SKELETON_CHECKBOX_SIZE}
                  sx={{ ...SKELETON_SURFACE_SX, borderRadius: `${SKELETON_CHECKBOX_RADIUS}px` }}
                />
              </Stack>
            )}

            <Skeleton
              variant="rounded"
              width={imageWidth}
              height={SKELETON_IMAGE_HEIGHT}
              sx={{
                ...SKELETON_SURFACE_SX,
                flexShrink: 0,
                marginRight: `${SKELETON_IMAGE_GAP}px`,
              }}
            />

            <Stack sx={{ flex: 1, minWidth: 0, rowGap: `${SKELETON_INFO_ROW_GAP}px` }}>
              <Skeleton variant="rectangular" height={SKELETON_LABEL_HEIGHT} sx={infoRadiusSx} />
              <Skeleton variant="rectangular" height={SKELETON_LABEL_HEIGHT} sx={infoRadiusSx} />
              <Stack direction="row" sx={{ columnGap: `${SKELETON_INFO_ROW_GAP}px` }}>
                {SKELETON_TAG_WIDTHS.map((width) => (
                  <Skeleton
                    key={width}
                    variant="rectangular"
                    width={width}
                    height={SKELETON_TAG_HEIGHT}
                    sx={infoRadiusSx}
                  />
                ))}
              </Stack>
            </Stack>
          </div>

          {isMobileLayout && (
            <div className={classes.orderProductMobileTotals}>
              <OrderProductTotalsSkeleton />
            </div>
          )}

          <div className={classes.orderProductButtons}>
            <div className={classes.orderProductColor}>
              <Skeleton variant="rounded" height={selectHeight} sx={SKELETON_SURFACE_SX} />
            </div>
            <div className={classes.orderProductDeliveryWindow}>
              <Skeleton variant="rounded" height={selectHeight} sx={SKELETON_SURFACE_SX} />
            </div>
          </div>
        </Stack>
      </div>

      <div className={classes.orderProductGridContainer}>
        <OrderProductGridSkeleton />
      </div>

      {!isMobileLayout && (
        <div className={classes.orderProductTotalsAndActionsWrapper}>
          <div className={classes.orderProductTotalsAndActionsContainer}>
            <OrderProductTotalsSkeleton />
            <Skeleton
              variant="rounded"
              width={SKELETON_ACTIONS_SIZE}
              height={SKELETON_ACTIONS_SIZE}
              sx={{ ...SKELETON_SURFACE_SX, flexShrink: 0 }}
            />
          </div>
        </div>
      )}
    </Stack>
  );
};

export default OrderProductItemSkeleton;
